import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  MenuItem,
  Select,
} from "@mui/material";
import { useDispatch, useSelector } from 'react-redux';
import { getMonthlyHeatMapStats } from '../../../../store/features/getHeatMapStats';
import { ReactComponent as DownArrow } from "../../../../assets/images/downarrow.svg";
import ChartTooltip from '../../Home/ChartTooltip';

// Replace the months array with:
const months = Array.from({ length: 30 }, (_, i) => i + 1); // [1, 2, 3, ..., 30]

// Define which day labels to show
const displayedDayLabels = [1, 10, 15, 25, 30];

const hours = [
  "12am",
  "1am",
  "2am",
  "3am",
  "4am",
  "5am",
  "6am",
  "7am",
  "8am",
  "9am",
  "10am",
  "11am",
  "12pm",
  "1pm",
  "2pm",
  "3pm",
  "4pm",
  "5pm",
  "6pm",
  "7pm",
  "8pm",
  "9pm",
  "10pm",
  "11pm",
];

const COLORS = {
  high: "#148DFD",
  medium: "#A0D4FF",
  low: "#D6EDFF",
  veryLow: "#F7F8F9",
};

const getIntensity = (totalActivity) => {
  if (totalActivity === 0) return 'veryLow';
  if (totalActivity >= 1 && totalActivity <= 2) return 'low';
  if (totalActivity >= 3 && totalActivity <= 5) return 'medium';
  if (totalActivity >= 6) return 'high';
  return 'veryLow';
};

const transformDataToMatrix = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) {
    return months.map(() => Array(24).fill('veryLow'));
  }

  const dailyData = {};
  apiData.forEach(item => {
    const dayKey = item.day;
    if (!dailyData[dayKey]) {
      dailyData[dayKey] = Array(24).fill(0);
    }
    dailyData[dayKey][item.hour] = item.totalActivity || 0;
  });

  return months.map(day => {
    const dayData = dailyData[day] || Array(24).fill(0);
    return dayData.map(activity => getIntensity(activity));
  });
};



export default function MonthlyChatTicketHeatmap({ groupId }) {
  const dispatch = useDispatch();
  const [tooltip, setTooltip] = useState({ visible: false, position: null, data: null });
  
  const { data: monthlyData, loading, error } = useSelector((state) => state.heatMapStats.monthlyData);
  
  const heatmapMatrix = transformDataToMatrix(monthlyData?.data);
  const heatmapData = monthlyData?.data || [];
  
  useEffect(() => {
    if (!groupId) return;
    dispatch(getMonthlyHeatMapStats({ group_id: groupId }));
  }, [dispatch, groupId]);

  // Get data for specific day and hour
  const getDayHourData = (dayIdx, hourIdx) => {
    const day = months[dayIdx];
    return heatmapData.find(data => data.day === day && data.hour === hourIdx) || {
      day: day,
      hour: hourIdx,
      hourLabel: hours[hourIdx],
      totalChats: 0,
      totalTickets: 0,
      totalActivity: 0
    };
  };

  // Handle mouse enter on heatmap box
  const handleMouseEnter = (event, dayIdx, hourIdx) => {
    const dayHourData = getDayHourData(dayIdx, hourIdx);
    const rect = event.currentTarget.getBoundingClientRect();
    const isLastHour = hourIdx === 23;
    
    setTooltip({
      visible: true,
      position: {
        x: isLastHour ? rect.left - 90 : rect.left + rect.width / 2,
        y: rect.top
      },
      data: {
        title: `Activity`,
        stats: [
          { label: 'Total Chats', value: dayHourData.totalChats },
          { label: 'Total Tickets', value: dayHourData.totalTickets }
        ]
      }
    });
  };



  // Handle mouse leave
  const handleMouseLeave = () => {
    setTooltip({ visible: false, position: null, data: null });
  };
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid #E5E7EB",
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{
                color: "#061522",
                fontFamily: "Inter, sans-serif",
                fontSize: "18px",
                fontWeight: 500,
                lineHeight: "24px",
                mb: 0.5,
              }}
            >
              Chat/Ticket Heatmap
            </Typography>
            <Typography
              sx={{
                color: "#405261",
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "20px",
              }}
            >
              Peak activity by month and hour
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 600 }}>
            {[...months].reverse().map((day, displayIdx) => {
              const monthIdx = months.length - 1 - displayIdx; 
              const shouldShowLabel = displayedDayLabels.includes(day);

              return (
                <Stack
                  key={day}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: "2px" }}
                >
                  <Box sx={{ width: "50px", flexShrink: 0 }}>
                    {shouldShowLabel && (
                      <Typography
                        sx={{
                          color: "#405261",
                          fontFamily: "Inter, sans-serif",
                          fontSize: "12px",
                          fontWeight: 400,
                          lineHeight: "10px",
                        }}
                      >
                        Day {day}
                      </Typography>
                    )}
                  </Box>
                  {heatmapMatrix[monthIdx].map((intensity, hourIdx) => (
                    <Box
                      key={`${day}-${hourIdx}`}
                      onMouseEnter={(e) => handleMouseEnter(e, monthIdx, hourIdx)}
                      onMouseLeave={handleMouseLeave}
                      sx={{
                        minWidth: { xs: "37px", md: "37px" },
                        height: { xs: "8px", md: "8px" },
                        flex: 1,
                        borderRadius: "8px",
                        bgcolor: COLORS[intensity],
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          transform: "scale(1.05)",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        },
                      }}
                    />
                  ))}
                </Stack>
              );
            })}
            <Stack direction="row" spacing={1} sx={{ mb: 1, pl: "60px" }}>
              {hours.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    minWidth: { xs: "37px", md: "37px" },
                    flex: 1,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#405261",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "16px",
                    }}
                  >
                    {hour}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{ mt: 3 }}
        >
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "16px",
            }}
          >
            Low
          </Typography>
          <Box
            sx={{
              width: "48px",
              height: "8px",
              borderRadius: "4px",
              bgcolor: COLORS.veryLow,
            }}
          />
          <Box
            sx={{
              width: "48px",
              height: "8px",
              borderRadius: "4px",
              bgcolor: COLORS.low,
            }}
          />
          <Box
            sx={{
              width: "48px",
              height: "8px",
              borderRadius: "4px",
              bgcolor: COLORS.medium,
            }}
          />
          <Box
            sx={{
              width: "48px",
              height: "8px",
              borderRadius: "4px",
              bgcolor: COLORS.high,
            }}
          />
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "16px",
            }}
          >
            High
          </Typography>
        </Stack>
      </CardContent>
      
      {tooltip.visible && tooltip.data && (
        <ChartTooltip
          title={tooltip.data.title}
          stats={tooltip.data.stats}
          position={tooltip.position}
        />
      )}
    </Card>
  );
}
